import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {Observable, map, tap} from "rxjs";
import {runWorkBulkBatches} from "../utils/work-bulk-batches.util";
import {environment} from "src/environments/environment";
import type {WorkSelectionReference} from "../../lists/interfaces/work-selection.interface";
import type {
  RecommendationBulkActionResponse,
  WorkBulkActionResponse,
} from "../interfaces/work-bulk-action.interface";
import {CacheService} from "./cache.service";
import {AuthService} from "../../auth/services/auth.service";

@Injectable({providedIn: "root"})
export class WorkBulkActionsService {
  private readonly apiDomain = environment.apiDomain;

  constructor(
    private readonly http: HttpClient,
    private readonly cacheService: CacheService,
    private readonly authService: AuthService
  ) {}

  addToList(
    userId: string,
    listId: string,
    works: WorkSelectionReference[]
  ): Observable<WorkBulkActionResponse> {
    return this.workBatches(works, (works) =>
      this.http.post<WorkBulkActionResponse>(
        `${this.apiDomain}/works/user/${userId}/list/${listId}/bulk`,
        {works}
      )
    );
  }

  deleteFromLibrary(
    userId: string,
    works: WorkSelectionReference[]
  ): Observable<WorkBulkActionResponse> {
    return this.workBatches(works, (works) =>
      this.http
        .delete<WorkBulkActionResponse>(
          `${this.apiDomain}/works/user/${userId}/bulk`,
          {body: {works}}
        )
        .pipe(
          tap((response) =>
            this.authService.removeLibraryWorks(
              userId,
              response.results
                .filter(
                  (result) =>
                    result.status === "success" || result.status === "notFound"
                )
                .map((result) => result.work)
            )
          )
        )
    );
  }

  unfollow(
    userId: string,
    works: WorkSelectionReference[]
  ): Observable<WorkBulkActionResponse> {
    return this.workBatches(works, (works) =>
      this.http.delete<WorkBulkActionResponse>(
        `${this.apiDomain}/works/user/${userId}/follow/bulk`,
        {body: {works}}
      )
    );
  }

  removeFromList(
    userId: string,
    listId: string,
    works: WorkSelectionReference[]
  ): Observable<WorkBulkActionResponse> {
    return this.workBatches(works, (works) =>
      this.http.delete<WorkBulkActionResponse>(
        `${this.apiDomain}/works/user/${userId}/list/${listId}/bulk`,
        {body: {works}}
      )
    );
  }

  hideRecommendations(
    recommendationIds: string[]
  ): Observable<RecommendationBulkActionResponse> {
    return runWorkBulkBatches(
      recommendationIds,
      (recommendationIds) =>
        this.withCacheInvalidation(
          this.http.patch<RecommendationBulkActionResponse>(
            `${this.apiDomain}/recommendations/bulk`,
            {recommendationIds, hidden: true}
          )
        ).pipe(map((response) => response.results)),
      (ids) =>
        ids.map((recommendationId) => ({
          recommendationId,
          status: "error" as const,
        }))
    ).pipe(map((results) => ({results})));
  }

  private workBatches(
    works: WorkSelectionReference[],
    request: (
      batch: WorkSelectionReference[]
    ) => Observable<WorkBulkActionResponse>
  ): Observable<WorkBulkActionResponse> {
    return runWorkBulkBatches(
      works,
      (batch) =>
        this.withCacheInvalidation(request(batch)).pipe(
          map((response) => response.results)
        ),
      (batch) => batch.map((work) => ({work, status: "error" as const}))
    ).pipe(map((results) => ({results})));
  }

  private withCacheInvalidation<T>(request: Observable<T>): Observable<T> {
    return request.pipe(
      tap(() => {
        void this.cacheService.clearCacheByPrefix([
          "history_",
          "ongoing_",
          "stats_",
          "list_",
          "followedWorks_",
          "recommendations_",
        ]);
      })
    );
  }
}
